import { useGetServicesMutation, useUpdateServicesMutation } from "src/services/servicesServices";
import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon, Select } from "@nubeteck/components";
import { ArrayUtils } from "@nubeteck/utils";
import { Layout, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { toastErrorStyle, useSuccessServiceUpdatedToast } from "src/constants";
import { useGetPaymentTypesQuery } from "src/services";
import { IPaymentTypes } from "@/Interfaces/options";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaServices";

interface IServicio {
  servicioId: number;
  servicioNombre: string;
  plazoTipos: IPaymentTypes[];
  ids: number[];
}

const Services: React.FC = () => {
  const [form] = Form.useForm<IServicio>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [servicioId, setServicioId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [getServices, { data, isLoading }] = useGetServicesMutation();
  const [
    updateServices,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, error: errorUpdate },
  ] = useUpdateServicesMutation();

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessServiceUpdatedToast = useSuccessServiceUpdatedToast();
  const [translatedData, setTranslatedData] = React.useState<{
    servicios: { [key: string]: string };
    obligatorios: { [key: string]: string };
    tiposPago: { [key: string]: string };
  }>({
    servicios: {},
    obligatorios: {},
    tiposPago: {},
  });

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const { data: tiposPago } = useGetPaymentTypesQuery("");

  const onClickEditarTipoPago = React.useCallback(
    (record: IServicio) => {
      setServicioId(record.servicioId);
      form.setFieldsValue({
        ids: record.plazoTipos.map((pl: IPaymentTypes) => pl.plazoId),
      });
      setDrawerOpen(true);
    },
    [form],
  );

  useEffect(() => {
    getServices();
  }, [getServices]);

  useEffect(() => {
    if (errorUpdate) toast.error(errorUpdate?.data?.message, toastErrorStyle);
    if (isSuccessUpdate) {
      showSuccessServiceUpdatedToast();
      getServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUpdate, errorUpdate, showSuccessServiceUpdatedToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      const hasTiposPago = tiposPago?.data && Array.isArray(tiposPago.data) && tiposPago.data.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData && !hasTiposPago) {
        setTranslatedData({
          servicios: {},
          obligatorios: {},
          tiposPago: {},
        });
        return;
      }

      const serviciosTranslations: { [key: string]: string | null } = {};
      const obligatoriosTranslations: { [key: string]: string | null } = {};
      const tiposPagoTranslations: { [key: string]: string | null } = {};

      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción de los servicios
      if (hasData) {
        data.data.forEach((record: IServicio) => {
          // Traducir servicio
          if (record.servicioNombre && !serviciosTranslations.hasOwnProperty(record.servicioNombre)) {
            serviciosTranslations[record.servicioNombre] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.servicioNombre, locale).then((translated) => {
                serviciosTranslations[record.servicioNombre] = translated;
              })
            );
          }

          // Traducir obligatorio (Si/No)
          const esObligatorio = (record as any).esObligatorio;
          const obligatorioText = esObligatorio ? "Si" : "No";
          if (!obligatoriosTranslations.hasOwnProperty(obligatorioText)) {
            obligatoriosTranslations[obligatorioText] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, obligatorioText, locale).then((translated) => {
                obligatoriosTranslations[obligatorioText] = translated;
              })
            );
          }

          // Traducir tipos de pago
          if (record.plazoTipos && Array.isArray(record.plazoTipos)) {
            record.plazoTipos.forEach((pl: IPaymentTypes) => {
              if (pl.descripcion && !tiposPagoTranslations.hasOwnProperty(pl.descripcion)) {
                tiposPagoTranslations[pl.descripcion] = null; // Marcador para evitar duplicados
                translationPromises.push(
                  _translateValue(SCREEN_NAME, pl.descripcion, locale).then((translated) => {
                    tiposPagoTranslations[pl.descripcion] = translated;
                  })
                );
              }
            });
          }
        });
      }

      // Recopilar tipos de pago del query (para el Select)
      if (hasTiposPago) {
        tiposPago.data.forEach((tp: IPaymentTypes) => {
          if (tp.descripcion && !tiposPagoTranslations.hasOwnProperty(tp.descripcion)) {
            tiposPagoTranslations[tp.descripcion] = null; // Marcador para evitar duplicados
            translationPromises.push(
              _translateValue(SCREEN_NAME, tp.descripcion, locale).then((translated) => {
                tiposPagoTranslations[tp.descripcion] = translated;
              })
            );
          }
        });
      }

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData({
        servicios: serviciosTranslations as { [key: string]: string },
        obligatorios: obligatoriosTranslations as { [key: string]: string },
        tiposPago: tiposPagoTranslations as { [key: string]: string },
      });
    };

    translateData();
  }, [data?.data, tiposPago?.data, locale]);

  const tiposPagoOptions = React.useMemo(() => {
    return (tiposPago?.data ?? []).map((tp: IPaymentTypes) => ({
      label: translatedData.tiposPago[tp.descripcion] || tp.descripcion,
      value: tp.plazoId,
    }));
  }, [tiposPago?.data, translatedData.tiposPago]);

  const columns = React.useMemo<ColumnsType<IServicio>>(
    () => [
      {
        title: settingsPage?.service_setting_table?.[0]?.label || "Servicio",
        dataIndex: "servicioNombre",
        render: (text: string) => translatedData.servicios[text] || text,
      },
      {
        title: settingsPage?.service_setting_table?.[1]?.label || "¿Es obligatorio?",
        dataIndex: "esObligatorio",
        render: (_: any, record: IServicio) => {
          const esObligatorio = (record as any).esObligatorio;
          const obligatorioText = esObligatorio ? "Si" : "No";
          return translatedData.obligatorios[obligatorioText] || obligatorioText;
        },
      },
      {
        title: settingsPage?.service_setting_table?.[2]?.label || "Tipo de pago",
        dataIndex: "plazoTipos",
        render: (plazoTipos: IPaymentTypes[]) => {
          return `${plazoTipos.map((pl: IPaymentTypes) => ` ${translatedData.tiposPago[pl.descripcion] || pl.descripcion}`)}`;
        },
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.servicioId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarTipoPago(record ?? 0),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarTipoPago, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 200, marginBottom: 16 }} />
          <Content style={{ marginTop: "16px" }}>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Content>
        </>
      ) : (
        <>
          <LargeHeadingStyled>{settingsPage?.service_nb || "Servicios"}</LargeHeadingStyled>
          <Content style={{ marginTop: "16px" }}>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IServicio>
                loading={isLoading || isLoadingUpdate}
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => `${v.servicioId} ${v.servicioNombre}`}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={settingsPage?.sevice_detail?.title || "Edición de servicio"}
                open={drawerOpen}
                onClose={() => {
                  setServicioId(0);
                  setDrawerOpen(false);
                }}
              >
                {" "}
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={(data) => updateServices({ ...data, servicioId })}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <Form.Item
                    name="ids"
                    label={settingsPage?.sevice_detail?.type || "Tipo de pago"}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: settingsPage?.required_field_message || "Este campo es requerido",
                      },
                    ]}
                  >
                    <Select size="large" options={tiposPagoOptions} mode="multiple" allowClear placeholder={settingsPage?.sevice_detail?.select_option || "Seleccione un tipo de pago"} />
                  </Form.Item>

                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    style={{ alignSelf: "flex-end" }}
                    onClick={() => setDrawerOpen(false)}
                  >
                    {settingsPage?.sevice_detail?.save_button || "Aceptar"}
                  </Button>
                </Form>
              </Drawer>
            </Card>
          </Content>
        </>
      )}
    </Flex>
  );
};

export default Services;
